import type { ComputeFileMd5Progress } from './compute-file-md5-main-thread';
import { computeFileMd5WithWorkerPool } from './md5-hash-worker-pool';

export const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB

export const STATUS_CHECK_INTERVAL = 2000; // 2 seconds
export const STATUS_RETRY_INTERVAL = 5000; // 5 seconds

export const MAX_RETRIES = 2;

export const isAbortError = (error: unknown): error is DOMException =>
  error instanceof DOMException && error.name === 'AbortError';

export const getFileNameWithoutExtension = (filename: string) =>
  filename.split('.').slice(0, -1).join('.') || filename;

export const getFileExtension = (filename: string) =>
  filename.split('.').pop()?.toLowerCase() || '';

export const isPdfFile = (filename: string) =>
  getFileExtension(filename) === 'pdf';

export const isCsvFile = (filename: string) =>
  getFileExtension(filename) === 'csv';

export const isOfficeModernFile = (filename: string) => {
  const ext = getFileExtension(filename);

  return [
    'xlsx',
    'xlsm',
    'xlsb',
    'docx',
    'docm',
    'pptx',
    'ppsx',
    'odp',
  ].includes(ext);
};

export const isOfficeLegacyFile = (filename: string) => {
  const ext = getFileExtension(filename);

  return ['doc', 'ppt', 'pps', 'pot'].includes(ext);
};

export const isImageFile = (filename: string) => {
  const extension = getFileExtension(filename);

  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
    extension
  );
};

export const isUnsupportedFormat = (filename?: string) => {
  if (!filename) return false;

  const extension = getFileExtension(filename);

  return ['xls', 'mov'].includes(extension);
};

const ALLOWED_EXTENSIONS = [
  'pdf',
  'csv',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'webp',
  'svg',
  'xlsx',
  'xlsm',
  'xlsb',
  'docx',
  'docm',
  'pptx',
  'ppsx',
  'odp',
  'doc',
  'ppt',
  'pps',
  'pot',
];

export const isFilePreviewSupported = (filename: string) =>
  ALLOWED_EXTENSIONS.includes(getFileExtension(filename));

export const downloadFileFromUrl = (url: string, fileName?: string) => {
  const link = document.createElement('a');
  link.href = url;
  if (fileName) link.download = fileName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export type { ComputeFileMd5Progress } from './compute-file-md5-main-thread';
export { computeFileMd5OnMainThread } from './compute-file-md5-main-thread';
export { resetMd5HashWorkerPoolForTests } from './md5-hash-worker-pool';

export const computeFileMd5 = async (
  file: File | Blob,
  chunkSize: number = CHUNK_SIZE,
  onProgress?: (progress: ComputeFileMd5Progress) => void
): Promise<Uint8Array> =>
  computeFileMd5WithWorkerPool(file, chunkSize, onProgress);

export const encodeMd5HashAsBase64 = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes));

export const decodeMd5HashFromBase64 = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index++)
    bytes[index] = binary.charCodeAt(index);

  return bytes;
};
